import * as React from 'react';

import { v4 as uuidv4 } from 'uuid';

import { captureContentUpdate } from '../../../../web/utils/oneDsUtil';
import { addGenevaLog, getMetaTagAttributeValue } from '../../../../web/utils/statelessUtil';
import { getString, isLoadResourcesEnabled } from '../../../../web/utils/stringLoader';
import { AwaMetaTagType, ContentAttribute, ErrorStrings } from '../../../models/constants/constants';

interface IErrorFallbackProps {
  fallbackUi?: React.ReactNode;
  errorId?: string | undefined;
}

interface IErrorFallbackState {
  hasError: boolean;
  errorId: string ;
}

/**
 * Setup error boundary for a React subtree.
 *
 * Any error captured is considered unexpected thus logged. Provide fallbackUi prop
 * to display a alternative UI if error happens. DO NOT attempt to use this as a control flow.
 */
export class ErrorFallback extends React.PureComponent<IErrorFallbackProps, IErrorFallbackState> {
  public constructor(props: IErrorFallbackProps) {
    super(props);
    const errorId = !!this.props.errorId ? this.props.errorId : uuidv4();
    this.state = { hasError: false, errorId: errorId };
  }

  public static getDerivedStateFromError(_error: Error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  public componentDidCatch(error: Error, _errorInfo: React.ErrorInfo): void {
    const errorMessage = error ? error.message : '';
    const errorStack = error ? error.stack : '';
    const buildVersion = getMetaTagAttributeValue(AwaMetaTagType.BuildVersion, ContentAttribute);
    const errorId = this.state.errorId;
    const logHandlerTags = {
        content: {
          contentId: 'reactAppErrorBoundary',
          contentName: 'reactAppErrorBoundary',
          errorId,
          errorMessage,
          buildVersion,
          formnm: 'maxFunnel',
          areaName: 'ErrorBoundary',
          url: `${window.location.href}`,
          errorStack,
        },
        behavior: 'OTHER',
      };
    captureContentUpdate(logHandlerTags);
    addGenevaLog(logHandlerTags);
    console.log(`errorId: ${errorId}`);
  }

  public render(): React.ReactNode {
    if (this.state.hasError) {
      if (this.props.fallbackUi) {
        return this.props.fallbackUi;
      }
      const localizedStringsFromDOM: any = window['ResponsiveNavReact'] && window['ResponsiveNavReact']['localStringsMap'];
      return <ErrorMessage
                localizedStringsFromDOM={localizedStringsFromDOM}
                errorId={this.state.errorId}
             />;
    }
    return this.props.children;
  }
}

interface IErrorMessageComponent {
  localizedStringsFromDOM: any
  errorId: string
}

export const ErrorMessage: React.FunctionComponent<IErrorMessageComponent> = (props: IErrorMessageComponent) => {
  const locStrings = props.localizedStringsFromDOM;
  let errorTitle = isLoadResourcesEnabled ? getString(ErrorStrings.title) : ErrorStrings.fallbackTitle;
  let errorDescription = isLoadResourcesEnabled ? getString(ErrorStrings.description) : ErrorStrings.fallbackDescription;
  let errorLog = isLoadResourcesEnabled ? getString(ErrorStrings.label) : ErrorStrings.fallbackLabel;
  if (locStrings) {
    errorTitle = locStrings[ErrorStrings.title] ?? errorTitle;
    errorDescription = locStrings[ErrorStrings.description] ?? errorDescription;
    errorLog = locStrings[ErrorStrings.label] ?? errorLog;
  }
  const containerStyle={
    width: '80%',
    paddingRight: '15px',
    paddingLeft: '15px',
    marginRight: 'auto',
    marginLeft: 'auto',
    fontFamily: '"Segoe UI Light","wf_segoe-ui_light",Arial,"Helvetica Neue",Verdana,Helvetica,Sans-Serif',
  };
  const titleStyle={
    fontSize: '26px',
    marginTop: '40px',
  };
  const descriptionStyle={
    fontSize: '16px',
    marginTop: '25px',
  };
  const errorLogStyle={
    fontSize: '12px',
    marginTop: '25px',
  };

  return (
    <div style={containerStyle}>
      <p style={titleStyle}>{errorTitle}</p>
      <p style={descriptionStyle}>{errorDescription}</p>
      <p style={errorLogStyle}>{`${errorLog}: ${props.errorId}`}</p>
    </div>
  );
};
